import { Box, Flex, GridItem, Text } from "@familyzone/component-library";
import React, { useEffect, useState } from "react";
import SignatureTreeStore from "../../../stores/SignatureTreeStore";
import Api from "../../../utils/Api";
import { RedFlagPill } from "../RedFlagPill/RedFlagPill";
import CardSpinner from "./CardSpinner";
import { FormatRedFlagDataForPills, keywordRegionMatch, RedFlagRawApiData, RedFlagRawData, SignatureKeyword } from "./RedFlagsCardHelper";
import { ApiDates } from "../../../utils/DateTimeUtil";
import SignatureTreeActions from "../../../actions/SignatureTreeActions";

export interface RedFlagsCardProps {
  user: string;
  dateFilters: ApiDates;
}

const RedFlagsCard = ({ user, dateFilters }: RedFlagsCardProps): JSX.Element => {
  const [riskSelectors, setRiskSelectors] = useState<RedFlagRawData>();
  const [keywords, setKeywords] = useState<SignatureKeyword[]>([]);
  const [errorState, setErrorState] = useState(false);

  const updateRedFlagsForRegion = (data: RedFlagRawData) => {
    if (data.search_indicators) {
      const updatedSearchIndicators: Record<string, number> = {};
      for (const key of Object.keys(data?.search_indicators)) {
        if (!keywords.find((k) => k.id === key)) {
          updatedSearchIndicators[key] = 0;
        } else {
          updatedSearchIndicators[key] = data?.search_indicators[key];
        }
      }

      return {
        ...data,
        search_indicators: updatedSearchIndicators,
      };
    } else {
      return data;
    }
  };

  useEffect(() => {
    /* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
    const onChangeSignatures = () => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const allKeywords = SignatureTreeStore.getKeywords();
      const filteredKeywords = keywordRegionMatch(allKeywords);
      setKeywords(filteredKeywords);
    };

    SignatureTreeStore.listen(onChangeSignatures);

    setTimeout(() => {
      SignatureTreeActions.fetch();
    }, 0);

    return () => {
      SignatureTreeStore.unlisten(onChangeSignatures);
    };
    /* eslint-enable */
  }, []);

  useEffect(() => {
    // Only load risk selectors after keywords are available
    if (keywords.length === 0) {
      return;
    }

    const loadRiskSelectorsData = (user: string) => {
      Api.get_analytics(
        `/surfwize/device/ajax/analytic/user/${user}/riskselector`,
        (response: RedFlagRawApiData) => {
          setRiskSelectors(updateRedFlagsForRegion(response.data));
        },
        (_: unknown) => {
          setErrorState(true);
        },
        { dateOverride: dateFilters }
      );
    };

    loadRiskSelectorsData(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilters, user, keywords]);

  const renderPills = (riskSelectors: RedFlagRawData) => {
    const data = FormatRedFlagDataForPills(riskSelectors, keywords, user);
    return data.map((value) => <RedFlagPill key={value.text} severity={value.severity} text={value.text} link={value.link} />);
  };

  return (
    <GridItem colSpan={4} background="neutrals.0" borderRadius="sm">
      <Box borderRadius="md" background="neutrals.0" paddingX="sp16" paddingY="sp24">
        <Text fontFamily="heading" fontWeight="500" fontSize="16px" color="text.title" as="h2">
          Red Flags
        </Text>
        <Flex display="flex" flexWrap="wrap">
          {errorState ? (
            <Text>There's a problem loading Red Flags for this user right now. Please try again later.</Text>
          ) : !riskSelectors || keywords.length === 0 ? (
            <CardSpinner />
          ) : (
            renderPills(riskSelectors)
          )}
        </Flex>
      </Box>
    </GridItem>
  );
};

export default RedFlagsCard;
